import { Button, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState, useRef } from "react";
import ToolPageLayout from "../../components/tools/ToolPageLayout";
import SignUpPromptModal from "../../components/tools/SignUpPromptModal";

const AudioTranscriber: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("transcript");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const outputFormats = [
    { id: "transcript", label: "Full Transcript", icon: "lucide:file-text" },
    { id: "summary", label: "Summary + Transcript", icon: "lucide:list" },
    { id: "notes", label: "Meeting Notes", icon: "lucide:clipboard-list" },
  ];

  const supportedFormats = ["MP3", "WAV", "M4A", "FLAC", "OGG", "WEBM"];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleTranscribe = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowSignUpModal(true);
    }, 2500);
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Audio Transcriber - YouWo AI",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Convert audio files and voice recordings to accurate text transcripts. Free AI-powered speech-to-text tool with speaker detection.",
  };

  const howItWorksSteps = [
    {
      title: "Upload Audio File",
      description: "Upload any audio file or record directly from your browser. Supports MP3, WAV, M4A, and more.",
    },
    {
      title: "AI Transcribes",
      description: "Our advanced speech recognition AI converts your audio to text with speaker detection.",
    },
    {
      title: "Get Your Transcript",
      description: "Download your accurate transcript with timestamps and speaker labels.",
    },
  ];

  const benefits = [
    {
      title: "99% Accuracy",
      description: "Industry-leading speech recognition powered by the latest AI models.",
      icon: "lucide:target",
    },
    {
      title: "Speaker Detection",
      description: "Automatically identifies and labels different speakers in your audio.",
      icon: "lucide:users",
    },
    {
      title: "100+ Languages",
      description: "Transcribe audio in over 100 languages with automatic language detection.",
      icon: "lucide:globe",
    },
    {
      title: "Timestamps",
      description: "Every sentence is timestamped for easy navigation and reference.",
      icon: "lucide:clock",
    },
    {
      title: "Live Recording",
      description: "Record directly from your browser with real-time transcription.",
      icon: "lucide:mic",
    },
    {
      title: "Export Options",
      description: "Download as TXT, DOCX, SRT subtitles, or save to your notes.",
      icon: "lucide:download",
    },
  ];

  const faqs = [
    {
      question: "What audio formats are supported?",
      answer: "We support all major audio formats including MP3, WAV, M4A, FLAC, OGG, WEBM, and more. You can also record audio directly in your browser.",
    },
    {
      question: "How accurate is the transcription?",
      answer: "Our AI achieves 99% accuracy for clear audio recordings. Accuracy may vary for audio with background noise, heavy accents, or technical terminology.",
    },
    {
      question: "Does it detect multiple speakers?",
      answer: "Yes! Our AI automatically detects and labels different speakers in your audio, making it perfect for meetings, interviews, and podcasts.",
    },
    {
      question: "What languages are supported?",
      answer: "We support over 100 languages including English, Spanish, French, German, Chinese, Japanese, and many more. The AI automatically detects the language.",
    },
    {
      question: "Is there a file size limit?",
      answer: "Free users can transcribe audio files up to 25MB (approximately 30 minutes). Sign up for free to transcribe longer recordings.",
    },
    {
      question: "Can I edit the transcript?",
      answer: "Yes! With a free YouWo AI account, you can edit transcripts, add notes, and correct any errors directly in our editor.",
    },
  ];

  const relatedTools = [
    {
      name: "YouTube Summarizer",
      description: "Transform YouTube videos into concise summaries with AI-generated timestamps.",
      href: "/tools/youtube-summarizer",
      icon: "lucide:youtube",
      iconColor: "text-red-500",
    },
    {
      name: "PDF Summarizer",
      description: "Turn lengthy PDFs and documents into clear, concise summaries instantly.",
      href: "/tools/pdf-summarizer",
      icon: "lucide:file-text",
      iconColor: "text-orange-500",
    },
    {
      name: "Meeting Notes Generator",
      description: "Convert meeting recordings into organized notes with action items.",
      href: "/tools/meeting-notes",
      icon: "lucide:users",
      iconColor: "text-indigo-500",
    },
    {
      name: "Lecture Notes AI",
      description: "Transform lecture recordings into comprehensive study notes.",
      href: "/tools/lecture-notes",
      icon: "lucide:graduation-cap",
      iconColor: "text-emerald-500",
    },
  ];

  return (
    <>
      <ToolPageLayout
        title="Free AI Audio Transcriber - Speech to Text Converter | YouWo AI"
        metaDescription="Convert audio files and voice recordings to accurate text transcripts instantly. Free AI-powered speech-to-text tool with speaker detection and 100+ language support."
        keywords="audio transcriber, speech to text, voice to text, transcription, meeting transcription, podcast transcription, free transcription tool"
        canonicalUrl="https://youwo.ai/tools/audio-transcriber"
        structuredData={structuredData}
        heroTitle="AI Audio Transcriber"
        heroSubtitle="Convert any audio file or voice recording to accurate text in seconds. Advanced AI with speaker detection, timestamps, and support for 100+ languages."
        heroIcon="lucide:mic"
        heroIconColor="text-blue-500"
        heroIconBg="bg-blue-50"
        howItWorksSteps={howItWorksSteps}
        benefitsTitle="Why Choose YouWo AI Audio Transcriber?"
        benefits={benefits}
        faqs={faqs}
        relatedTools={relatedTools}
      >
        {/* Tool Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          {/* Record Button */}
          <div className="flex justify-center mb-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                isRecording
                  ? "bg-red-500 animate-pulse"
                  : "bg-gradient-to-br from-cornflowerblue to-blue-600 hover:shadow-lg"
              }`}
              onClick={() => {
                if (isRecording) {
                  setHasRecorded(true);
                }
                setIsRecording(!isRecording);
              }}
            >
              <Icon
                icon={isRecording ? "lucide:square" : "lucide:mic"}
                className="text-white text-4xl"
              />
            </motion.button>
          </div>

          <p className="text-center text-gray-500 mb-6">
            {isRecording
              ? "Recording... Click to stop"
              : hasRecorded
                ? "Recording ready! Click Transcribe to continue"
                : "Click to start recording"}
          </p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or upload a file</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
              isDragging
                ? "border-cornflowerblue bg-blue-50"
                : selectedFile
                ? "border-green-400 bg-green-50"
                : "border-gray-200 hover:border-cornflowerblue"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="audio/*"
              onChange={handleFileSelect}
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Icon icon="lucide:file-audio" className="text-green-600 text-xl" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="light"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Icon icon="lucide:upload-cloud" className="text-gray-400 text-3xl" />
                <p className="text-gray-600">
                  Drag & drop or click to upload
                </p>
                <p className="text-xs text-gray-400">
                  Supports: {supportedFormats.join(", ")}
                </p>
              </div>
            )}
          </div>

          {/* Output Format Selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-6">
            <span className="text-sm text-gray-600 font-medium">Output Format:</span>
            <div className="flex flex-wrap gap-2">
              {outputFormats.map((format) => (
                <Chip
                  key={format.id}
                  variant={selectedFormat === format.id ? "solid" : "bordered"}
                  color={selectedFormat === format.id ? "primary" : "default"}
                  className={`cursor-pointer transition-all ${
                    selectedFormat === format.id
                      ? "bg-cornflowerblue text-white"
                      : "border-gray-200 text-gray-600 hover:border-cornflowerblue"
                  }`}
                  startContent={<Icon icon={format.icon} className="text-sm" />}
                  onClick={() => setSelectedFormat(format.id)}
                >
                  {format.label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <Button
              color="primary"
              size="lg"
              className="w-full bg-cornflowerblue h-14 font-semibold"
              isLoading={isLoading}
              isDisabled={!selectedFile && !isRecording && !hasRecorded}
              onClick={handleTranscribe}
            >
              {isLoading ? "Transcribing..." : "Transcribe Audio"}
            </Button>
          </div>
        </div>
      </ToolPageLayout>

      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        toolName="Audio Transcript"
      />
    </>
  );
};

export default AudioTranscriber;
